import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_ENV, VITE_APP_RELEASE } from '../../lib/constants.ts';
import { api } from '../../utils/api.ts';
import { useAnalytics } from '../analytics/hooks/use-analytics.ts';

const POLL_INTERVAL_MS = 5_000 * 60;

const RELOADED_FOR_RELEASE_KEY = 'version-watch:reloaded-for-release';

const isMonitoredEnv = APP_ENV === 'production' || APP_ENV === 'staging';

function reloadedForRelease(): string | undefined {
    try {
        return sessionStorage.getItem(RELOADED_FOR_RELEASE_KEY) ?? undefined;
    } catch {
        return undefined;
    }
}

function markReloadedForRelease(release: string): void {
    try {
        sessionStorage.setItem(RELOADED_FOR_RELEASE_KEY, release);
    } catch {
        // sessionStorage blocked: the guard is skipped.
    }
}

/**
 * Detects a stale frontend (an open tab older than the deployed server) and
 * silently reloads onto the new build on the next navigation.
 */
export function useVersionWatch(): void {
    const sendEvent = useAnalytics();
    const { pathname } = useLocation();

    const shouldRun = isMonitoredEnv && !!VITE_APP_RELEASE;

    const { data } = api.version.getCurrent.useQuery(undefined, {
        enabled: shouldRun,
        refetchOnMount: false,
        refetchOnWindowFocus: true,
        refetchInterval: POLL_INTERVAL_MS,
    });

    const stale =
        shouldRun &&
        !!data &&
        data.env === APP_ENV &&
        data.release !== 'unknown' &&
        data.release !== VITE_APP_RELEASE;

    const reportedReleaseRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (!stale || reportedReleaseRef.current === data.release) {
            return;
        }
        reportedReleaseRef.current = data.release;
        sendEvent('stale_client_detected', {
            client_release: VITE_APP_RELEASE ?? 'unknown',
            server_release: data.release,
            app_env: APP_ENV,
        });
    }, [stale, data, sendEvent]);

    // Reload on the next navigation. The URL has already moved, so this loads the
    // destination on the fresh build.
    const prevPathnameRef = useRef(pathname);
    useEffect(() => {
        const navigated = prevPathnameRef.current !== pathname;
        prevPathnameRef.current = pathname;
        if (stale && navigated && reloadedForRelease() !== data.release) {
            markReloadedForRelease(data.release);
            window.location.reload();
        }
    }, [stale, pathname, data]);
}
